import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useApolloClient } from '@apollo/client';

import {
  ActivitiesComponentType,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
} from 'global/constants';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import type { InteractiveLessonSectionComponentFragment } from '../lesson-builder.graphql.generated';
import { useActivityBuilderContext } from '../activity-builder.context';
import {
  sortComponents,
  getNewSortValue,
  getFirstSortValue,
} from '../activity-builder.utils';
import {
  reorderComponent,
  resetSectionInCache,
} from '../activity-builder.apollo.utils';
import {
  canMoveIntoColumn,
  moveInsideColumn,
  moveOutsideColumn,
  findComponent,
} from './droppable.utils';
import {
  getPreceedingComponentSort,
  handleMoveBetweenColumns,
} from './draggable.utils';
import { useGetInteractiveLessonDataFromCache } from '../activity-builder.hooks';
import { useBatchComponentUpdateContext } from '../components/shared/batch-component-update.context';
import { getSortByDirection } from 'lib/between/between';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { ComponentColumns } from '../components/component.constants';
import {
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
  ARROW_UP,
  ENTER,
  ESCAPE,
  SPACEBAR,
  TAB,
} from 'approot/shared/ally/keyboard.hooks';
import * as Styled from './draggable.style';
import { ColumnData } from './droppable.constants';
import { useActivityBuilderApolloContext } from '../activity-builder.apollo.context';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from '../activity-builder.constants';

type Props = {
  children: ReactNode;
  id: string;
  type: ActivitiesComponentType;
  typename: string;
  sectionId: string;
  index: number;
  sortedComponents: InteractiveLessonSectionComponentFragment[];
  column?: ColumnData;
  onClick: (
    e: MouseEvent<HTMLDivElement>,
    el: HTMLDivElement,
    id: string
  ) => void;
};

let lastOperation: {
  newSortValue: string;
  columnComponentId?: string;
  columnId?: string;
} | null = null;

export const Draggable = ({
  children,
  id,
  type,
  typename,
  column,
  index,
  sectionId,
  sortedComponents,
  onClick,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const client = useApolloClient();

  const {
    keyboardDragComponentId,
    setKeyboardDragComponentId,
    activeComponent,
    setActiveComponent,
  } = useActivityBuilderContext();
  const { interactiveSectionClone, setInteractiveSectionClone } =
    useActivityBuilderApolloContext();

  const { showError } = useSnackbar();

  const endKeyboardDragMode = useCallback(() => {
    setKeyboardDragComponentId('');
    lastOperation = null;
  }, [setKeyboardDragComponentId]);

  const restoreOriginalData = useCallback(() => {
    endKeyboardDragMode();
    // add back the interactive lesson clone data
    if (interactiveSectionClone) {
      resetSectionInCache({
        client,
        section: interactiveSectionClone,
      });
    }
  }, [client, endKeyboardDragMode, interactiveSectionClone]);

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { getSection } = useGetInteractiveLessonDataFromCache();

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type,
    item: () => {
      return {
        el: ref.current,
        id,
        typename,
        column,
        isColumnComponent: type === ACTIVITIES_COMPONENT_TYPE_COLUMNS,
      };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const handleReorder = useCallback(
    (sort: string, column: ColumnData | undefined) => {
      const component = findComponent(sortedComponents, id);
      if (component) {
        reorderComponent({
          client,
          id: component.id,
          typename: component?.__typename,
          sort,
        });
      }
      lastOperation = {
        newSortValue: sort,
        columnComponentId: column?.componentId,
        columnId: column?.id,
      };
    },
    [id, client, sortedComponents]
  );

  const handleArrowUp = useCallback(() => {
    const section = getSection(sectionId);
    // is inside a column and in the first position
    if (column && index === 0) {
      const newSortValue = getPreceedingComponentSort(section, column);
      lastOperation = moveOutsideColumn(client, section, newSortValue, {
        id,
        column,
        typename,
      });
      return;
    }

    // is the previous component a column
    const previousComponent = sortedComponents[index - 1];
    const currentComponent = sortedComponents[index];
    if (
      previousComponent?.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS &&
      canMoveIntoColumn(currentComponent.type)
    ) {
      // move item into column
      const columnsComponent = previousComponent as ComponentColumns;
      const column = columnsComponent.columns?.[0];
      const components = column?.components || [];
      const newSortValue = getNewSortValue({
        components,
        componentId: components.at(-1)?.id,
      });
      lastOperation = moveInsideColumn(
        client,
        section,
        newSortValue,
        { id, typename },
        {
          id: column?.columnId || '',
          componentId: columnsComponent.id,
        }
      );
      return;
    }

    if (index >= 0) {
      handleReorder(
        getSortByDirection(sortedComponents, index, DIRECTION_LEFT),
        column
      );
    }
  }, [
    client,
    column,
    id,
    index,
    sortedComponents,
    getSection,
    handleReorder,
    sectionId,
    typename,
  ]);

  const handleArrowDown = useCallback(() => {
    const section = getSection(sectionId);
    // is inside a column and in the last position
    if (column && index === sortedComponents.length - 1) {
      // find the next item outside column in the section
      const components = sortComponents(section?.components || []);
      const newSortValue = getNewSortValue({
        components,
        componentId: column.componentId,
      });
      lastOperation = moveOutsideColumn(client, section, newSortValue, {
        id,
        column,
        typename,
      });
      return;
    }

    if (index < sortedComponents.length - 1) {
      // is the next component in a column component
      const currentComponent = sortedComponents[index];
      const nextComponent = sortedComponents[index + 1];
      if (
        nextComponent.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS &&
        canMoveIntoColumn(currentComponent.type)
      ) {
        // move item into column
        // get top sort value in column
        const columnsComponent = nextComponent as ComponentColumns;
        const column = columnsComponent.columns?.[0];
        const components = column?.components || [];
        const newSortValue = getFirstSortValue(components);
        lastOperation = moveInsideColumn(
          client,
          section,
          newSortValue,
          { id, typename },
          {
            id: column?.columnId || '',
            componentId: columnsComponent.id,
          }
        );
        return;
      }

      handleReorder(
        getSortByDirection(sortedComponents, index, DIRECTION_RIGHT),
        column
      );
    }
  }, [
    client,
    column,
    id,
    index,
    sortedComponents,
    getSection,
    handleReorder,
    sectionId,
    typename,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      const target = e.target as HTMLElement;
      // make sure events are only processed on container
      if (!!!target.dataset.draggable) return;

      const key = e.key;
      const isKeyboardDragMode = keyboardDragComponentId;

      // start keyboard drag mode
      if (!isKeyboardDragMode && key === SPACEBAR && e.target) {
        e.preventDefault();
        e.stopPropagation();
        setKeyboardDragComponentId(id);
        setInteractiveSectionClone(getSection(sectionId));
        return;
      }

      if (!isKeyboardDragMode) return;

      // exit keyboard drag mode
      if (key === ESCAPE) {
        e.preventDefault();
        restoreOriginalData();
        return;
      }

      // if keyboard dragging do nothing on tab
      if (key === TAB) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (key === SPACEBAR) {
        e.preventDefault();
        e.stopPropagation();

        // save changes stored in lastOperation
        if (lastOperation) {
          updateComponentProperties({
            input: {
              sectionId,
              componentId: id,
              parentDetails: {
                parentComponentId: lastOperation.columnComponentId || null,
                columnId: lastOperation.columnId || null,
              },
              componentData: {
                sort: lastOperation.newSortValue,
              },
            },
            typename: typename,
            onCompleted: endKeyboardDragMode,
            onError: () => {
              restoreOriginalData();
              showError(
                'There was an error saving the content. Please try again'
              );
            },
          });
        }
        endKeyboardDragMode();
        return;
      }

      if (column && (key === ARROW_LEFT || key === ARROW_RIGHT)) {
        lastOperation = handleMoveBetweenColumns(
          client,
          getSection(sectionId),
          id,
          column,
          key
        );
      }

      if (key === ARROW_UP) {
        e.preventDefault();
        e.stopPropagation();
        handleArrowUp();
        return;
      }

      if (key === ARROW_DOWN) {
        e.preventDefault();
        e.stopPropagation();
        handleArrowDown();
        return;
      }
    },
    [
      client,
      handleArrowUp,
      handleArrowDown,
      updateComponentProperties,
      showError,
      typename,
      endKeyboardDragMode,
      setKeyboardDragComponentId,
      keyboardDragComponentId,
      column,
      id,
      getSection,
      sectionId,
      restoreOriginalData,
      setInteractiveSectionClone,
    ]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      if (!!!target.dataset.draggable) return;

      const key = e.key;
      if (key === ENTER) {
        e.preventDefault();
        e.stopPropagation();
        onClick(e as any, ref.current!, id);
        return;
      }

      if (key === ESCAPE) {
        e.preventDefault();
        setActiveComponent(undefined);
        return;
      }
    },
    [id, onClick, setActiveComponent]
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
    if (keyboardDragComponentId === id && ref.current) {
      ref.current.focus();
    }
    //eslint-disable-next-line
  }, []);

  return (
    <Styled.Draggable
      data-selector="component-draggable"
      ref={ref}
      id={`component-${id}`}
      className={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      data-draggable="true"
      tabIndex={0}
      $isDragging={isDragging}
      $isActive={activeComponent?.id === id}
      $isKeyboardFocussed={keyboardDragComponentId === id}
      $isColumn={type === ACTIVITIES_COMPONENT_TYPE_COLUMNS}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyPress}
      onClick={e => onClick(e, ref.current!, id)}
    >
      {type === ACTIVITIES_COMPONENT_TYPE_COLUMNS ? (
        <Styled.DraggableHandle
          ref={node => {
            drag(node);
          }}
          data-selector="icon-grab"
        >
          <ActivityBuilderInlineSvg id="icon_grab" width={16} height={16} />
        </Styled.DraggableHandle>
      ) : activeComponent?.id !== id ? (
        <Styled.DraggableSurfaceHandle
          ref={node => {
            drag(node);
          }}
        >
          <Styled.DraggableSurfaceTooltipTarget>
            Click to Edit. Hold to Drag.
          </Styled.DraggableSurfaceTooltipTarget>
        </Styled.DraggableSurfaceHandle>
      ) : null}
      {children}
    </Styled.Draggable>
  );
};
